import { useState, useSyncExternalStore } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ToolIcon } from '@caios/ui';
import './delivery-nav.css';

/** Harbor's pages, as one rail entry: pill tabs above each Delivery page (Adam 11-02, option 2). */
export const DELIVERY_TABS = [
  { to: '/delivery/reports', label: 'Reports' },
  { to: '/delivery/content', label: 'Content' },
  { to: '/delivery/search', label: 'Search' },
  { to: '/delivery/social', label: 'Social' },
  { to: '/delivery/ads', label: 'Ads' },
  { to: '/delivery/proofboard', label: 'Proofboard' },
  { to: '/delivery/connections', label: 'Connections' },
] as const;

type DClient = { id: string; name: string; relationship: string; status: string; logo: string | null };
const live = (c: DClient) => c.status !== 'churned' && c.status !== 'prospect';

// One chosen client for every Delivery page, kept while you move between sections and across visits.
const KEY = 'caios.delivery.client';
const subs = new Set<() => void>();
let picked = (() => { try { return localStorage.getItem(KEY) ?? ''; } catch { return ''; } })();
const subscribe = (f: () => void) => { subs.add(f); return () => { subs.delete(f); }; };
const pick = (id: string) => { picked = id; try { localStorage.setItem(KEY, id); } catch { /* private window */ } subs.forEach((f) => f()); };

export function useDeliveryClient() {
  const clients = useQuery({ queryKey: ['delivery-clients'], queryFn: async () => {
    const r = await fetch('/api/v1/clients', { credentials: 'include' });
    if (!r.ok) throw new Error(`Request failed (${r.status})`);
    return (await r.json()) as DClient[];
  } });
  const chosen = useSyncExternalStore(subscribe, () => picked);
  const all = clients.data ?? [];
  const account = all.some((c) => c.id === chosen) ? chosen : (all.find(live)?.id ?? all[0]?.id ?? '');
  return { clients, account, client: all.find((c) => c.id === account), setAccount: pick };
}

/** A client's logo or photo filling its circle; the first letter when there is none. */
export function ClientMark({ c, size = 26 }: { c: Pick<DClient, 'name' | 'logo'>; size?: number }) {
  return <span className="dl-mark" style={{ width: size, height: size, fontSize: size * 0.42 }} aria-hidden="true">{c.logo ? <img src={c.logo} alt="" /> : c.name.slice(0, 1).toUpperCase()}</span>;
}

/** Delivery's side panel: live clients (our brands first), search, drafts waiting on each; former ones folded away. */
export function DeliveryClients({ d, testid }: { d: ReturnType<typeof useDeliveryClient>; testid: string }) {
  const [q, setQ] = useState('');
  const [former, setFormer] = useState(false);
  const waiting = useQuery({ queryKey: ['delivery-waiting'], queryFn: async () => {
    const r = await fetch('/api/v1/delivery/waiting', { credentials: 'include' });
    return r.ok ? ((await r.json()) as { account_id: string; reports: number }[]) : [];
  } });
  const n = new Map(waiting.data?.map((w) => [w.account_id, w.reports]));
  const term = q.trim().toLowerCase();
  const all = (d.clients.data ?? []).filter((c) => !term || c.name.toLowerCase().includes(term));
  const ours = all.filter((c) => live(c) && c.relationship !== 'client');
  const theirs = all.filter((c) => live(c) && c.relationship === 'client');
  const gone = all.filter((c) => !live(c));
  const row = (c: DClient) => (
    <button key={c.id} type="button" className="cx-nav-item dl-client" aria-current={d.account === c.id} onClick={() => pick(c.id)} data-testid={testid}>
      <ClientMark c={c} /><span className="dl-name">{c.name}</span>{n.get(c.id) ? <span className="dl-badge" title="Client reports waiting to be published">{n.get(c.id)}</span> : null}
    </button>
  );
  return (
    <nav className="cx-panel cx-side cx-rise dl-clients" aria-label="Clients">
      <div className="cx-side-head"><ToolIcon name="harbor" size={28} /><div><b>Delivery</b><span className="cx-label">Everything we do for a client</span></div></div>
      <label className="dl-search">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>
        <input type="search" placeholder="Search clients" aria-label="Search clients" value={q} onChange={(e) => setQ(e.target.value)} data-testid="dl-search" />
      </label>
      <div className="cx-side-group dl-list">
        {ours.length > 0 && <><div className="cx-label"><span>Our brands</span></div>{ours.map(row)}</>}
        {theirs.length > 0 && <><div className="cx-label"><span>Clients</span></div>{theirs.map(row)}</>}
        {gone.length > 0 && <button type="button" className="dl-former" aria-expanded={former || !!term} onClick={() => setFormer(!former)} data-testid="dl-former">Former clients and prospects · {gone.length}</button>}
        {(former || !!term) && gone.map(row)}
      </div>
    </nav>
  );
}

/** The sections as pill tabs above the page; the chosen client stays as you switch. */
export function DeliveryTabs() {
  return (
    <div className="dl-tabs" role="navigation" aria-label="Delivery sections">
      {DELIVERY_TABS.map((t) => <Link key={t.to} to={t.to} className="dl-tab" activeProps={{ 'aria-current': 'page' }} data-testid={`dl-tab-${t.to}`}>{t.label}</Link>)}
    </div>
  );
}
